import React, { useRef, useEffect, useState } from 'react'
import { useField } from 'formik'
import Inputmask from 'inputmask'
import styled from '@emotion/styled'
import AnimateHeight from 'react-animate-height'
import searchIcon from '../assets/search.svg'
import { Form, Input, Skeleton } from 'antd'
import { CopyButton } from './CopyButton'

export const TextFieldInput = React.memo(({ children: Children, ...props }: any) => {
  const ref = useRef<any>(null)
  const [displayErrorMessage, setDisplayErrorMessage] = useState(props.errorMessage)

  useEffect(() => {
    if (!props.errorMessage) setTimeout(() => setDisplayErrorMessage(''), 500)
    else setDisplayErrorMessage(props.errorMessage)
  }, [props.errorMessage])

  useEffect(() => {
    if (props.mask && ref.current && !props.loading) {
      let mask = props.mask
      if (mask === 'cpf') mask = { mask: '999.999.999-99' }
      else if (mask === 'cnpj') mask = { mask: '99.999.999/9999-99' }
      else if (mask === 'cpf-cnpj') {
        mask = {
          mask: ['999.999.999-99', '99.999.999/9999-99'],
          keepStatic: true,
        }
      } else if (mask === 'cep') mask = { mask: '99999-999' }
      else if (mask === 'telefone') {
        mask = { mask: ['(99) 9999-9999', '(99) 99999-9999'], keepStatic: true }
      } else if (mask === 'currency') {
        mask = {
          alias: 'numeric',
          prefix: 'R$ ',
          groupSeparator: '.',
          radixPoint: ',',
          digits: 2,
          digitsOptional: false,
          rightAlign: false,
          unmaskAsNumber: true,
          removeMaskOnSubmit: true,
        }
      } else mask = { mask }

      // For Ant Design Input, we need to access the actual input element
      const inputElement = ref.current.input || ref.current
      Inputmask(mask).mask(inputElement)
    }
  }, [props.mask, props.loading])

  function handlePaste(e) {
    if (props.onPaste) props.onPaste(e)
    if (props.onChange) props.onChange(e)
  }

  // Extract props that shouldn't be passed to Input
  const { errorMessage, label, mask, loading, showCopyButton, ...inputProps } = props

  return (
    <Form.Item
      label={label}
      validateStatus={errorMessage ? 'error' : ''}
      layout="vertical"
      style={{ marginBottom: '0.4rem' }}
    >
      {loading ? (
        <Skeleton.Input active style={{ width: '100%' }} />
      ) : Children ? (
        React.cloneElement(Children, inputProps)
      ) : (
        <Input
          ref={ref}
          {...inputProps}
          onPaste={handlePaste}
          status={errorMessage ? 'error' : ''}
          addonAfter={
            showCopyButton ? (
              <CopyButton value={inputProps.value} size="small" />
            ) : (
              inputProps.suffix
            )
          }
        />
      )}
      <AnimateHeight height={errorMessage ? 'auto' : 0}>
        <Styles.ErrorMessage>{displayErrorMessage || errorMessage}</Styles.ErrorMessage>
      </AnimateHeight>
    </Form.Item>
  )
})

export const TextField = React.memo((props: any) => {
  const [field, meta] = useField(props.name)

  if (!props.name) throw new Error('É necessário passar o parâmetro "name" para um "TextField"')
  const errorMessage = meta.touched && meta.error ? meta.error : null

  return (
    <div>
      <TextFieldInput
        {...field}
        {...props}
        onChange={(e) => {
          if (props.onChange) props.onChange(e)
          field.onChange(e)
        }}
        errorMessage={errorMessage}
      />
      {}
    </div>
  )
})

const Styles = {
  ErrorMessage: styled('div')`
    color: #ff4d4f;
    font-size: 14px;
    margin-top: 4px;
  `,
}

export const TextFieldSearch = ({ ...props }) => {
  return <Input.Search {...props} style={{ maxWidth: 500 }} allowClear />
}
